import React, { useState, useEffect, useRef, useCallback } from 'react';
import logger from '../lib/logger';
import { 
  MessageCircle, 
  Send, 
  Loader2, 
  X, 
  Sparkles,
  Bot,
  User,
  Trash2,
  ChevronDown,
  ChevronUp,
  Zap,
  Youtube,
  Music2,
  Instagram,
  HelpCircle,
  Mic,
  Square
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import * as api from '@/lib/api';

const AIAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      fetchHistory();
      fetchSuggestions();
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchHistory = async () => {
    try {
      const response = await api.getAIHistory(30);
      if (response.data.messages) {
        setMessages(response.data.messages.map(msg => ({
          id: msg.id,
          role: msg.role,
          content: msg.content,
          action: msg.action,
          action_result: msg.action_result,
          timestamp: new Date(msg.created_at)
        })));
      }
    } catch (error) {
      logger.error('Failed to fetch history:', error);
    }
  };

  const fetchSuggestions = async () => {
    try {
      const response = await api.getAISuggestions();
      setSuggestions(response.data.suggestions || []);
    } catch (error) {
      logger.error('Failed to fetch suggestions:', error);
    }
  };

  const sendMessage = async (text = inputValue) => {
    if (!text.trim() || loading) return;

    const userMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: text,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setLoading(true);

    try {
      const response = await api.sendAIMessage(text);
      
      const assistantMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.data.response,
        action: response.data.action,
        action_result: response.data.action_result,
        suggestions: response.data.suggestions,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Update suggestions if provided
      if (response.data.suggestions) {
        setSuggestions(response.data.suggestions);
      }

      // Handle special actions
      if (response.data.action_result?.data?.redirect) {
        toast.info('Redirecionando para configurações...');
      }

    } catch (error) {
      toast.error('Erro ao enviar mensagem');
      logger.error(error);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const clearHistory = async () => {
    if (!confirm('Limpar todo o histórico de conversa?')) return;
    
    try {
      await api.clearAIHistory();
      setMessages([]);
      toast.success('Histórico limpo');
    } catch (error) {
      toast.error('Erro ao limpar histórico');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const transcribeAndSend = async () => {
    const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
    audioChunksRef.current = [];
    if (!blob.size) return;
    setTranscribing(true);
    try {
      const form = new FormData();
      form.append('file', blob, 'voice.webm');
      const res = await api.transcribeAudio(form);
      const text = (res.data.text || '').trim();
      if (text) {
        sendMessage(text);
      } else {
        toast.info('Não consegui entender o áudio. Tente novamente.');
      }
    } catch (error) {
      toast.error('Erro ao transcrever o áudio');
      logger.error(error);
    } finally {
      setTranscribing(false);
    }
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      toast.error('Seu navegador não suporta gravação de áudio');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      audioChunksRef.current = [];
      const recorder = new MediaRecorder(stream);
      recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = () => { stopStream(); transcribeAndSend(); };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setRecording(true);
    } catch (error) {
      toast.error('Não foi possível acessar o microfone. Verifique as permissões.');
      logger.error(error);
    }
  };

  const stopRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') recorder.stop();
    setRecording(false);
  };

  const toggleRecording = () => {
    if (recording) stopRecording();
    else startRecording();
  };

  useEffect(() => () => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') recorder.stop();
    stopStream();
  }, []);

  // Render **bold** and line breaks as React nodes (no innerHTML, no XSS surface)
  const formatMessage = (content) => {
    const lines = String(content || '').split('\n');
    return lines.flatMap((line, li) => {
      const parts = line.split(/(\*\*[^*]+\*\*)/g).filter(Boolean).map((part, pi) =>
        part.startsWith('**') && part.endsWith('**')
          ? <strong key={`${li}-${pi}`}>{part.slice(2, -2)}</strong>
          : <React.Fragment key={`${li}-${pi}`}>{part}</React.Fragment>
      );
      return li < lines.length - 1 ? [...parts, <br key={`br-${li}`} />] : parts;
    });
  };

  const renderActionResult = (action, result) => {
    if (!result?.data) return null;

    if (action === 'list_accounts' && result.data.accounts) {
      const { accounts } = result.data;
      return (
        <div className="mt-2 space-y-2">
          {Object.entries(accounts).map(([platform, accs]) => (
            accs.length > 0 && (
              <div key={platform} className="flex items-center gap-2">
                {platform === 'youtube' && <Youtube className="w-4 h-4 text-red-500" />}
                {platform === 'tiktok' && <Music2 className="w-4 h-4 text-pink-500" />}
                {platform === 'instagram' && <Instagram className="w-4 h-4 text-purple-500" />}
                <span className="text-sm">
                  {accs.map(a => a.username || a.display_name).join(', ')}
                </span>
              </div>
            )
          ))}
        </div>
      );
    }

    if (action === 'search_podcasts' && result.data.podcasts) {
      return (
        <div className="mt-2 space-y-1">
          {result.data.podcasts.slice(0, 3).map((podcast) => (
            <div key={podcast.id || podcast.title} className="text-xs bg-muted/50 p-2 rounded">
              {podcast.title?.substring(0, 50)}...
            </div>
          ))}
          {result.data.podcasts.length > 3 && (
            <span className="text-xs text-muted-foreground">
              +{result.data.podcasts.length - 3} mais
            </span>
          )}
        </div>
      );
    }

    return null;
  };

  if (!isOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        className="fixed right-4 sm:right-6 bottom-20 lg:bottom-6 h-12 w-12 sm:h-14 sm:w-14 rounded-full shadow-lg glow-primary z-50"
        data-testid="ai-assistant-toggle"
      >
        <Sparkles className="w-5 h-5 sm:w-6 sm:h-6" />
      </Button>
    );
  }

  return (
    <Card 
      className={`fixed right-3 sm:right-6 bottom-20 lg:bottom-6 w-[calc(100vw-1.5rem)] sm:w-[340px] bg-card border-border shadow-2xl z-[60] transition-all duration-300 flex flex-col overflow-hidden ${
        isMinimized ? 'h-[52px]' : 'h-[min(440px,calc(100dvh-7rem))] sm:h-[min(520px,calc(100dvh-8rem))]'
      }`}
      data-testid="ai-assistant-panel"
    >
      {/* Header */}
      <CardHeader className="py-2 px-3 border-b border-border flex flex-row items-center justify-between shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
            <Bot className="w-4 h-4 text-primary" />
          </div>
          <div className="min-w-0">
            <CardTitle className="text-sm font-medium leading-tight truncate">Assistente Viral Clipes</CardTitle>
            <span className="text-[11px] text-muted-foreground">Comandos por texto</span>
          </div>
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setIsMinimized(!isMinimized)}
          >
            {isMinimized ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setIsOpen(false)}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>

      {!isMinimized && (
        <>
          {/* Messages */}
          <ScrollArea className="flex-1 min-h-0 p-3">
            {messages.length === 0 ? (
              <div className="text-center py-6">
                <Sparkles className="w-9 h-9 mx-auto text-primary/50 mb-3" />
                <p className="text-muted-foreground text-sm mb-3">
                  Olá! Sou seu assistente para gerenciar vídeos e contas.
                </p>
                <p className="text-[11px] text-muted-foreground mb-3">
                  Experimente comandos como:
                </p>
                <div className="flex flex-col gap-1.5">
                  {suggestions.slice(0, 4).map((suggestion) => (
                    <Button
                      key={suggestion}
                      variant="outline"
                      size="sm"
                      className="text-xs h-auto py-1.5 px-2 whitespace-normal text-left justify-start leading-snug"
                      onClick={() => sendMessage(suggestion)}
                    >
                      {suggestion}
                    </Button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-lg px-2.5 py-2 ${
                        message.role === 'user'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted'
                      }`}
                    >
                      <div className="flex items-center gap-1.5 mb-1">
                        {message.role === 'user' ? (
                          <User className="w-3 h-3" />
                        ) : (
                          <Bot className="w-3 h-3" />
                        )}
                        <span className="text-[11px] opacity-70">
                          {message.role === 'user' ? 'Você' : 'Assistente'}
                        </span>
                      </div>
                      <div className="text-[13px] leading-snug" data-testid="ai-message-content">
                        {formatMessage(message.content)}
                      </div>
                      {message.action && (
                        <Badge variant="outline" className="mt-1.5 text-[10px]">
                          <Zap className="w-3 h-3 mr-1" />
                          {message.action}
                        </Badge>
                      )}
                      {renderActionResult(message.action, message.action_result)}
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-muted rounded-lg px-2.5 py-2">
                      <Loader2 className="w-4 h-4 animate-spin text-primary" />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </ScrollArea>

          {/* Quick Suggestions */}
          {messages.length > 0 && suggestions.length > 0 && (
            <div className="px-3 py-1.5 border-t border-border shrink-0">
              <div className="flex flex-wrap gap-1">
                {suggestions.slice(0, 3).map((suggestion) => (
                  <Button
                    key={suggestion}
                    variant="ghost"
                    size="sm"
                    className="text-[11px] h-6 px-2 max-w-full truncate bg-muted/40"
                    onClick={() => sendMessage(suggestion)}
                    disabled={loading}
                  >
                    {suggestion}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="px-3 py-2.5 border-t border-border shrink-0 bg-card">
            <div className="flex gap-1.5">
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 flex-shrink-0"
                onClick={clearHistory}
                title="Limpar histórico"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
              <Button
                variant={recording ? 'destructive' : 'ghost'}
                size="icon"
                className={`h-9 w-9 flex-shrink-0 ${recording ? 'animate-pulse' : ''}`}
                onClick={toggleRecording}
                disabled={transcribing || loading}
                title={recording ? 'Parar e enviar' : 'Falar com a IA'}
                data-testid="ai-mic-btn"
              >
                {transcribing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : recording ? (
                  <Square className="w-4 h-4" />
                ) : (
                  <Mic className="w-4 h-4" />
                )}
              </Button>
              <Input
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyPress}
                onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ block: 'nearest' }), 150)}
                placeholder={recording ? 'Gravando... toque no quadrado para enviar' : transcribing ? 'Transcrevendo áudio...' : 'Digite ou fale um comando...'}
                className="flex-1 h-9 text-base sm:text-sm"
                disabled={loading}
                enterKeyHint="send"
                data-testid="ai-input"
              />
              <Button
                onClick={() => sendMessage()}
                disabled={loading || !inputValue.trim()}
                className="h-9 w-9 flex-shrink-0"
                data-testid="ai-send-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
            <div className="flex items-center justify-center mt-1">
              <Button
                variant="link"
                size="sm"
                className="text-[11px] h-6 text-muted-foreground"
                onClick={() => sendMessage('ajuda')}
              >
                <HelpCircle className="w-3 h-3 mr-1" />
                Ver comandos disponíveis
              </Button>
            </div>
          </div>
        </>
      )}
    </Card>
  );
};

export default AIAssistant;
